import React, { useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { useRouter } from 'next/router';
import { omit } from 'lodash-es';
import { ListPageFiltersWrapper } from '..';
import { InputFieldV2 } from '../form-controls';

import styles from './pinnedFilters.style';

export const PinnedFilters = ({
  pinnedFilters,
  filtersConfig,
  wrapperStyles = {},
  useFilterItemContainer = false,
  handleChangeSingleFilter = () => {},
  handleChangeMultiFilter = () => {},
}) => {
  const router = useRouter();
  const pinnedFiltersSet = useMemo(
    () => new Set(pinnedFilters || []),
    [pinnedFilters]
  );

  const handleClearAllFilters = () => {
    const keysToRemove = filtersConfig.flatMap((filter) => [
      filter?.key,
      ...(filter?.queryKeys || []),
    ]);
    router.push({
      pathname: router.pathname,
      query: omit(router.query, keysToRemove),
    });
  };

  const hasQueryParam = (key) =>
    Object.prototype.hasOwnProperty.call(router.query, key) &&
    router.query[key] !== undefined;

  const visibleFilters = filtersConfig.filter(
    (filter) =>
      // show filter when pinned or when its key/queryKeys exist in URL query params (with non-undefined value)
      pinnedFiltersSet.has(filter?.key) ||
      (filter?.queryKeys
        ? filter.queryKeys.some((k) => hasQueryParam(k))
        : hasQueryParam(filter?.key))
  );

  return (
    <ListPageFiltersWrapper styles={wrapperStyles}>
      <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {visibleFilters.map((filter) => {
          const inputField = (
            <InputFieldV2
              type={filter?.type}
              label={filter?.label}
              width={filter?.width}
              minWidth={filter?.minWidth}
              options={filter?.options}
              disabled={filter?.disabled}
              hasSelectAllOption={filter?.hasSelectAllOption}
              value={filter?.value}
              is24hr={filter?.is24hr}
              views={filter?.views}
              format={filter?.format}
              onChange={(value) =>
                filter?.isMulti
                  ? handleChangeMultiFilter(value, filter?.key, filter)
                  : handleChangeSingleFilter(value, filter?.key)
              }
              {...filter}
            />
          );

          if (!useFilterItemContainer) {
            return (
              <React.Fragment key={filter?.key}>{inputField}</React.Fragment>
            );
          }

          return (
            <Box
              key={filter.key}
              sx={{
                display: 'inline-flex',
                flex: '0 1 auto',
                width: filter.width || 'auto',
                minWidth: filter.minWidth || 'auto',
                maxWidth: '100%',
              }}
            >
              {inputField}
            </Box>
          );
        })}
        {visibleFilters.length > 0 && (
          <Button
            variant="text"
            onClick={handleClearAllFilters}
            sx={styles.clearAllButtonStyles}
          >
            Clear filters
          </Button>
        )}
      </Box>
    </ListPageFiltersWrapper>
  );
};
